import React, { FC, useEffect, useState } from 'react';
import { Button, Card, Table, Space, Modal, Form, Col, Row, Input, Select, Typography } from 'antd';
// import { useSelector } from 'react-redux';
import { LeftOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AppDispatch, CombinedStates } from '../../core/types';
import Main from '../../components/layout/Main';
import { updateOtherUser } from '../../core/user/userApi';
import { FinishedFailedProps, valuesProps } from './types';
import { School } from '../../core/school/types';
import { roleOptions } from '../../core/utils/helpers';

import './userEdit.scss';

type SchoolFormValues = Omit<School, 'id'>;

const selectOptions = roleOptions.map(role => ({
    label: role.name,
    value: role.id,
}));

const { Text } = Typography;

const passwordRules = {
    length: (val: string) => val.length >= 8,
    uppercase: (val: string) => /[A-Z]/.test(val),
    number: (val: string) => /[0-9]/.test(val),
    symbol: (val: string) => /[!@#$%^&*(),.?":{}|<>]/.test(val),
};

const UserEdit = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const {
        singleUser,
        isFetching,
        userData,
    } = useSelector((state:CombinedStates) => state.user);

    const {
        schools,
    } = useSelector((state:CombinedStates) => state.school);

    const [passwordModalVisible, setPasswordModalVisible] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [selectedRoleId, setSelectedRoleId] = useState<number | undefined>(singleUser?.role_id);

    const passwordValidationResults = {
        length: passwordRules.length(newPassword),
        uppercase: passwordRules.uppercase(newPassword),
        number: passwordRules.number(newPassword),
        symbol: passwordRules.symbol(newPassword),
    };

    const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;

    const canEditRoles = (editorRoleId: number, targetRoleId: number) => {
        // Super Administratorius gali viską
        if (editorRoleId === 1) return true;

        // Administratorius gali keisti tik ne super'ius
        if (editorRoleId === 2 && targetRoleId !== 1) return true;

        // Visi kiti nieko keisti negali
        return false;
    };
    const roleSelectDisabled = !canEditRoles(userData?.role_id || 0, singleUser?.role_id || 0);

    const [form] = Form.useForm();

    const onFinish = (values: Omit<School, 'id'>) => {
        if (!singleUser?.id) return;
        dispatch(updateOtherUser({ ...values, id: singleUser?.id }));
    };

    const onFinishFailed:FC<FinishedFailedProps> = ({ errorFields }) => {
        form.scrollToField(errorFields[0]?.name);
        return null;
    };

    const toolbar = [{ label: 'Item 1', link: '/abc', iconName: 'home' }];

    const handleBack = () => {
        navigate(-1); // grįžta į prieš tai buvusį puslapį
    };

    return (
        <Main
            className="edit-page"
            title={<div>Vartotojo redagavimas</div>}
            headerTitle={<div>Vartotojo: <span>&quot;{singleUser?.email}&quot;</span> redagavimas</div>}
            menuWidth={350}
            menuPlacement="left"
            toolbar={toolbar}
            showFooter={false}
            floatingHeader={false}
            footerContent={<div />}
            floatingFooter
            backLink={false}
            breadcrumb={null}>
            <Button
                type="text"
                icon={<LeftOutlined />}
                onClick={handleBack}
                style={{ marginBottom: '16px' }}>
                Grįžti atgal
            </Button>
            <Card>
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={onFinish}
                    // onFinishFailed={onFinishFailed}
                    initialValues={{
                        first_name: singleUser?.first_name,
                        last_name: singleUser?.last_name,
                        email: singleUser?.email,
                        phone: singleUser?.phone,
                        role_id: singleUser?.role_id,
                        school_id: singleUser?.school_id,
                    }}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="El. paštas (prisijungimo vardas)"
                                name="email"
                                rules={[{ type: 'email', message: 'Netinkamas el. pašto formatas' }]}>
                                <Input disabled placeholder="Įveskite el. paštą" />
                            </Form.Item>
                            <Form.Item
                                label="Vardas"
                                name="first_name"
                                rules={[{ required: true, message: 'Įveskite vardą.' }]}>
                                <Input placeholder="Įveskite vardą" />
                            </Form.Item>

                            <Form.Item
                                label="Pavardė"
                                name="last_name"
                                rules={[{ required: true, message: 'Įveskite pavardę.' }]}>
                                <Input placeholder="Įveskite pavardę" />
                            </Form.Item>

                            <Form.Item
                                label="Telefono numeris"
                                name="phone"
                                rules={[
                                    { required: true, message: 'Įveskite telefono numerį' },
                                    { pattern: /^\+?\d{6,20}$/, message: 'Įveskite teisingą telefono numerį' },
                                ]}>
                                <Input placeholder="Įveskite telefono numerį" />
                            </Form.Item>

                            <Form.Item
                                label="Rolė"
                                name="role_id"
                                rules={[{ required: true, message: 'Pasirinkite rolę.' }]}>
                                <Select
                                    options={selectOptions}
                                    placeholder="Pasirinkite rolę"
                                    disabled={roleSelectDisabled}
                                    onChange={value => setSelectedRoleId(value)} />
                            </Form.Item>
                            {selectedRoleId === 5 && (
                                <Form.Item
                                    label="Mokykla"
                                    name="school_id"
                                    rules={[{ required: true, message: 'Pasirinkite mokyklą.' }]}>
                                    <Select
                                        options={schools.map(school => ({
                                            label: school.name, // arba school.name jeigu kitaip vadinasi
                                            value: school.id,
                                        }))}
                                        placeholder="Pasirinkite mokyklą" />
                                </Form.Item>
                            )}
                            <Form.Item>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={isFetching}>
                                    Atnaujinti
                                </Button>
                            </Form.Item>
                            <div className="text-right">
                                <Button onClick={() => setPasswordModalVisible(true)} type="link" className="button-link">
                                    Keisti slaptažodį?
                                </Button>
                            </div>

                        </Col>
                    </Row>
                </Form>
            </Card>
            <Modal
                title="Keisti slaptažodį"
                open={passwordModalVisible}
                onCancel={() => setPasswordModalVisible(false)}
                onOk={() => {
                    // Čia gali iškviesti update password API
                    dispatch(updateOtherUser({ id: singleUser?.id || 0, password: newPassword }));
                    setPasswordModalVisible(false);
                    setNewPassword('');
                    setConfirmPassword('');
                }}
                okText="Keisti slaptažodį"
                cancelText="Atšaukti"
                okButtonProps={{ disabled: !Object.values(passwordValidationResults).every(Boolean) || !passwordsMatch }}>
                <Form layout="vertical">
                    <div>
                        <Text strong>Slaptažodyje turi būti:</Text>
                        <ul style={{ paddingLeft: 20 }}>
                            <li style={{ color: passwordValidationResults.length ? 'green' : 'gray' }}>
                                Bent 8 simboliai
                            </li>
                            <li style={{ color: passwordValidationResults.uppercase ? 'green' : 'gray' }}>
                                Bent 1 didžioji raidė
                            </li>
                            <li style={{ color: passwordValidationResults.number ? 'green' : 'gray' }}>
                                Bent 1 skaičius
                            </li>
                            <li style={{ color: passwordValidationResults.symbol ? 'green' : 'gray' }}>
                                Bent 1 simbolis {'(! # $ % & ( ) * + , - . / : ; < = > ? @ [ \\ ] ^ _ { | } ~)'}
                            </li>
                        </ul>
                    </div>
                    <Form.Item label="Naujas slaptažodis">
                        <Input.Password
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            placeholder="Įveskite naują slaptažodį" />
                    </Form.Item>
                    <Form.Item label="Pakartokite slaptažodį">
                        <Input.Password
                            value={confirmPassword}
                            onChange={e => setConfirmPassword(e.target.value)}
                            placeholder="Pakartokite slaptažodį"
                            status={confirmPassword && !passwordsMatch ? 'error' : ''} />
                    </Form.Item>
                </Form>
            </Modal>

        </Main>
    );
};

export default UserEdit;

UserEdit.propTypes = {};
